import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow, useLoadScript } from "@react-google-maps/api";
import { markerStyle, legendFor } from "./markerStatus";

/**
 * The map behind every map view. One component, mounted by both /listings and /pipeline, so
 * the two cannot drift the way /map drifted from both of them.
 *
 * A PIN IS A BUILDING. The server aggregates by property_id because 95% of listings share a
 * coordinate with another listing — 3,573 collision points, worst 268 pins on one address —
 * so a pin per listing draws 268 markers nobody can tell apart and 267 of them unclickable.
 * Hovering a pin shows the BUILDING: how many units, what they cost. Clicking it hands the
 * property_id back so the list beside the map can narrow to that building's units.
 *
 * It owns no data. Markers, selection and hover all come in as props and all changes go out
 * as callbacks, because the list beside it has to stay in step — hover a row, its pin lifts;
 * hover a pin, its rows highlight.
 */
const LIBRARIES = [];
const DEFAULT_CENTER = { lat: 40.7128, lng: -74.006 };

export default function ListingMap({
  markers = [],
  hoveredPropertyId = null,
  selectedPropertyId = null,
  onHover = () => {},
  onSelect = () => {},
  // Changes whenever the plotted SET changes — search or page. See the fit effect.
  fitKey = null,
  // "status" for the pipeline board, which carries one; "side" for the listings page, whose
  // rows do not. Painting by a dimension the data lacks gives every pin the same grey.
  colorBy = "status",
  loading = false,
  height = "100%",
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });
  const mapRef = useRef(null);
  const [infoFor, setInfoFor] = useState(null);
  // A hover card vanishes the moment you move towards it. Clicking pins it open so
  // the unit links inside can actually be reached.
  const [pinned, setPinned] = useState(false);

  // Fit the viewport to the results ONCE PER SEARCH, keyed on something the parent controls.
  //
  // ⚠️ It used to key on the markers themselves (`length:firstId`), which is a loop: fitting
  // fires a bounds change, the bounds change refetches, the refetch returns a slightly
  // different set, the different set looks like a new key, so it fits again. With a
  // non-deterministic LIMIT on the server it never converged and took the browser tab with
  // it. The server's ORDER BY fixed the non-determinism; this fixes the cycle, and both are
  // needed — a stable result set still must not re-fit on every fetch, or panning would snap
  // the user back to where they started.
  const fittedFor = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !markers.length || fitKey == null || fittedFor.current === fitKey) return;
    fittedFor.current = fitKey;
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    map.fitBounds(bounds, 32);
  }, [fitKey, markers]);

  const legend = useMemo(() => legendFor(markers, colorBy), [markers, colorBy]);
  const hovered = infoFor || markers.find((m) => m.property_id === hoveredPropertyId) || null;

  if (!isLoaded) {
    return <div style={{ ...shell, height }}><div style={note}>Loading map…</div></div>;
  }

  return (
    <div style={{ ...shell, height }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={DEFAULT_CENTER}
        zoom={12}
        onLoad={(map) => { mapRef.current = map; }}
        // Clearing the selection on a map click keeps our state honest when Google closes an
        // InfoWindow behind React's back — which is what clicking one of its own POIs does.
        onClick={() => { setPinned(false); setInfoFor(null); onSelect(null); }}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        {/* No clusterer. This map plots one PAGE — at most ~25 listings, fewer pins once
            they are grouped by building — so there is nothing to cluster, and clustering
            would actively hide the handful of pins you came to look at behind a bubble. */}
        {markers.map((m) => {
          const lit = m.property_id === hoveredPropertyId || m.property_id === selectedPropertyId;
          const { color } = markerStyle(m, colorBy);
          return (
            <Marker
              key={m.property_id}
              position={{ lat: m.lat, lng: m.lng }}
              onMouseOver={() => { setInfoFor(m); onHover(m.property_id); }}
              onMouseOut={() => { if (!pinned) setInfoFor(null); onHover(null); }}
              onClick={() => { setPinned(true); setInfoFor(m); onSelect(m.property_id); }}
              // A symbol, not a pin: a pin is anchored at its TIP and a symbol at its centre,
              // so mixing the two makes a highlighted marker appear to jump and hide under
              // its neighbours. Everything here is a circle; only size and colour change.
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: lit ? 11 : Math.min(7 + Math.log2(m.unit_count || 1) * 1.6, 13),
                fillColor: color,
                fillOpacity: lit ? 1 : 0.85,
                strokeColor: "#fff",
                strokeWeight: lit ? 3 : 1.5,
              }}
              // Stacked buildings are rare now (one pin per address) but a highlighted pin
              // must still never sit behind an idle one.
              zIndex={lit ? window.google.maps.Marker.MAX_ZINDEX + 1 : undefined}
            />
          );
        })}

        {hovered && (
          <InfoWindow
            // A NEW window per building. Without the key this is one long-lived instance that
            // only ever has setPosition called on it, and the first time anything closes it
            // behind React's back it never reopens.
            key={hovered.property_id}
            position={{ lat: hovered.lat, lng: hovered.lng }}
            options={{ disableAutoPan: true, pixelOffset: new window.google.maps.Size(0, -8) }}
            onCloseClick={() => { setPinned(false); setInfoFor(null); }}
          >
            <BuildingCard m={hovered} />
          </InfoWindow>
        )}
      </GoogleMap>

      {legend.length > 0 && (
        <div style={legendBox}>
          {legend.map((s) => (
            <span key={s.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <i style={{ width: 9, height: 9, borderRadius: "50%", background: s.color,
                          display: "inline-block", border: "1px solid #fff",
                          boxShadow: "0 0 0 1px rgba(0,0,0,.2)" }} />
              {s.label}
            </span>
          ))}
        </div>
      )}

      {/* Never let the map imply it is showing everything. */}
      {loading && <div style={banner}>Loading…</div>}

    </div>
  );
}

function BuildingCard({ m }) {
  const money = (n) => (n == null ? null : `$${Math.round(n).toLocaleString()}`);
  const range = m.price_min == null ? null
    : m.price_min === m.price_max ? money(m.price_min)
    : `${money(m.price_min)} – ${money(m.price_max)}`;
  return (
    <div style={{ minWidth: 190, fontSize: 13, lineHeight: 1.45 }}>
      <div style={{ fontWeight: 700, marginBottom: 3 }}>{m.address}</div>
      <div style={{ color: "#555" }}>
        {m.unit_count} {m.unit_count === 1 ? "unit" : "units"}
        {m.sides?.length === 1 ? ` · ${m.sides[0] === "S" ? "Sale" : "Rental"}` : ""}
      </div>
      {range && <div style={{ color: "#555" }}>{range}</div>}
      {m.cap_rate != null && <div style={{ color: "#555" }}>Cap rate {m.cap_rate.toFixed(2)}%</div>}
      {/* With a list beside the map a pin only has to point at it. The pipeline board has no
          list in map view, so the units are named here and link straight through — otherwise
          a pin on that page is a dot you can admire and not open. */}
      {m.units?.length ? (
        <div style={{ marginTop: 6, borderTop: "1px solid #eee", paddingTop: 5 }}>
          {m.units.slice(0, 6).map((u) => (
            <a key={u.id} href={`/predict/${u.id}`} target="_blank" rel="noopener noreferrer"
               style={{ display: "block", color: "#1976d2", textDecoration: "none", fontSize: 12 }}>
              {u.unit_number ? `#${u.unit_number}` : `Listing ${u.id}`}
              {u.price != null ? ` · $${Math.round(u.price).toLocaleString()}` : ""}
            </a>
          ))}
          {m.units.length > 6 && (
            <div style={{ fontSize: 11, color: "#888" }}>+{m.units.length - 6} more</div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 5, fontSize: 11, color: "#1976d2" }}>Click to see the units</div>
      )}
    </div>
  );
}

const shell = { position: "relative", width: "100%", borderRadius: 8, overflow: "hidden",
                border: "1px solid #e0e0e0", background: "#eceff1" };
const note = { display: "flex", alignItems: "center", justifyContent: "center",
               height: "100%", color: "#666", fontSize: 13 };
const legendBox = { position: "absolute", bottom: 12, left: 12, display: "flex", gap: 12,
                    flexWrap: "wrap", maxWidth: "70%", background: "rgba(255,255,255,.94)",
                    padding: "6px 10px", borderRadius: 6, fontSize: 11, color: "#333",
                    boxShadow: "0 1px 4px rgba(0,0,0,.2)" };
const banner = { position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
                 background: "rgba(38,50,56,.92)", color: "#fff", padding: "5px 12px",
                 borderRadius: 14, fontSize: 12, whiteSpace: "nowrap" };
